import { Component, HostBinding, Inject, Input, OnChanges, OnDestroy, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CultureService } from '../../culture/culture.service';

export interface ShareButtonDef {
    url: string;
    icon: string;
    label?: string;
    /// The accessible name and tooltip. It starts with the visible label, so a voice user can say
    /// what they see.
    title: string;
}

export interface ShareButtonUserDef {
    type: string;
    label?: string;
    icon?: string;
}

export interface ShareButton {
    type: string;
    url: string;
    label: string;
    icon: string;
    title: string;
}

export type ShareButtonsList = string | Array<string | ShareButtonUserDef>;

/// Labels are each network's own word for the action today: X calls it a post, Pinterest a save.
const shareButtonsDef: Record<string, ShareButtonDef> = {
    facebook: {
        url: 'https://www.facebook.com/sharer/sharer.php?u=%URL%',
        icon: 'facebook-f',
        label: 'Share',
        title: 'Share on Facebook',
    },
    x: {
        url: 'https://x.com/intent/post?text=%TITLE%&url=%URL%',
        icon: 'x',
        label: 'Post',
        title: 'Post on X',
    },
    pinterest: {
        url: 'https://www.pinterest.com/pin/create/button/?url=%URL%&media=%IMAGE%&description=%TITLE%',
        icon: 'pinterest',
        label: 'Save',
        title: 'Save to Pinterest',
    },
    // The product name and the link in one message; a bare link arrives without a word of context.
    whatsapp: {
        url: 'https://wa.me/?text=%TEXT%',
        icon: 'whatsapp',
        label: 'Send',
        title: 'Send on WhatsApp',
    },
    linkedin: {
        url: 'https://www.linkedin.com/shareArticle?title=%TITLE%&url=%URL%',
        icon: 'fab fa-linkedin',
        label: 'Share',
        title: 'Share on LinkedIn',
    },
    ok: {
        url: 'https://connect.ok.ru/offer?url=%URL%&title=%TITLE%&imageUrl=%IMAGE%',
        icon: 'fab fa-odnoklassniki',
        label: 'Share',
        title: 'Share on OK',
    },
    vk: {
        url: 'https://vk.com/share.php?url=%URL%',
        icon: 'fab fa-vk',
        label: 'Share',
        title: 'Share on VK',
    },
    telegram: {
        url: 'https://t.me/share/url?url=%URL%&text=%TITLE%',
        icon: 'fas fa-paper-plane',
        label: 'Share',
        title: 'Share on Telegram',
    },
    email: {
        url: 'mailto:?to=&subject=%TITLE%&body=%URL%',
        icon: 'envelope',
        label: 'Share',
        title: 'Share by email',
    },
    // Not a url: the device's own share sheet where there is one, otherwise a copied link.
    link: {
        url: '',
        icon: 'link',
        label: 'Copy link',
        title: 'Copy link',
    }
};

/// Templates written before Twitter became X keep working.
const typeAliases: Record<string, string> = {
    twitter: 'x',
};

@Component({
    selector: 'app-share-buttons',
    templateUrl: './share-buttons.component.html',
    styleUrls: ['./share-buttons.component.scss']
})
export class ShareButtonsComponent implements OnChanges, OnDestroy {
    items: ShareButton[] = [];

    @Input() buttons: ShareButtonsList = [];

    @Input() showIcons = true;

    @Input() showLabels = true;

    @Input() pageUrl: string;

    @Input() pageTitle: string;

    @Input() pageImage: string;

    @HostBinding('class.share-buttons') classShareLinks = true;

    /// Phones, Safari and Chrome on Windows have a share sheet, which lists every app the visitor
    /// uses. Where there is none (Firefox on a desktop, for one), the button copies the link: it
    /// used to do nothing there but write a console warning.
    readonly canShareNatively: boolean;

    copied = false;

    private copiedTimer: any;

    /// The buttons' words (shareButtonsDef's labels and titles) in the page's language, by their English.
    private readonly words: Record<string, string> = {
        'Share': this.culture.t('share', 'Share'),
        'Post': this.culture.t('share-post', 'Post'),
        'Save': this.culture.t('share-save', 'Save'),
        'Send': this.culture.t('share-send', 'Send'),
        'Copy link': this.culture.t('share-copy-link', 'Copy link'),
        'Share on Facebook': this.culture.t('share-on-facebook', 'Share on Facebook'),
        'Post on X': this.culture.t('share-post-on-x', 'Post on X'),
        'Save to Pinterest': this.culture.t('share-save-to-pinterest', 'Save to Pinterest'),
        'Send on WhatsApp': this.culture.t('share-send-on-whatsapp', 'Send on WhatsApp'),
        'Share on LinkedIn': this.culture.t('share-on-linkedin', 'Share on LinkedIn'),
        'Share on OK': this.culture.t('share-on-ok', 'Share on OK'),
        'Share on VK': this.culture.t('share-on-vk', 'Share on VK'),
        'Share on Telegram': this.culture.t('share-on-telegram', 'Share on Telegram'),
        'Share by email': this.culture.t('share-by-email', 'Share by email'),
    };

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private culture: CultureService,
    ) {
        this.canShareNatively = isPlatformBrowser(this.platformId) && typeof navigator.share === 'function';
    }

    get linkLabel(): string {
        if (this.copied) {
            return this.culture.t('share-link-copied', 'Link copied');
        }

        return this.canShareNatively ? this.culture.t('share', 'Share') : this.culture.t('share-copy-link', 'Copy link');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.buttons || changes.pageUrl || changes.pageTitle || changes.pageImage) {
            this.makeItems();
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.copiedTimer);
    }

    private makeItems(): void {
        let value = this.buttons;

        if (typeof value === 'string') {
            value = value.split(',');
        }

        this.items = value.map(x => {
            const userDef: ShareButtonUserDef = typeof x === 'string' ? {type: x} : x;
            const type = typeAliases[userDef.type] || userDef.type;
            const def = shareButtonsDef[type];

            if (!def) {
                return null;
            }

            return {
                type,
                url: def.url ? this.makeShareUrl(def.url) : '',
                label: typeof userDef.label === 'string' ? userDef.label : (this.words[def.label] || def.label),
                icon: typeof userDef.icon === 'string'? userDef.icon : def.icon,
                title: this.words[def.title] || def.title,
            } as ShareButton;
        }).filter(x => x !== null);
    }

    private currentUrl(): string {
        const here = isPlatformBrowser(this.platformId) ? window.location.href : '';

        return this.pageUrl || here;
    }

    private makeShareUrl(baseUrl: string): string {
        const pageUrl = this.currentUrl();
        const pageTitle = this.pageTitle || '';
        const pageImage = this.pageImage || '';
        const text = pageTitle ? `${pageTitle} ${pageUrl}` : pageUrl;

        return baseUrl
            .replace('%URL%', encodeURIComponent(pageUrl))
            .replace('%TITLE%', encodeURIComponent(pageTitle))
            .replace('%IMAGE%', encodeURIComponent(pageImage))
            .replace('%TEXT%', encodeURIComponent(text));
    }

    shareLink(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const url = this.currentUrl();

        if (this.canShareNatively) {
            // Closing the sheet without picking an app rejects as well; there is nothing to report.
            navigator.share({ title: this.pageTitle, url }).catch(() => { });
            return;
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url).then(
                () => this.showCopied(),
                () => this.copyWithSelection(url) && this.showCopied());
            return;
        }

        if (this.copyWithSelection(url)) {
            this.showCopied();
        }
    }

    private showCopied(): void {
        this.copied = true;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => this.copied = false, 2000);
    }

    /// For a browser without the async clipboard, or a page not served over https.
    private copyWithSelection(text: string): boolean {
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.top = '0';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();

        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        }

        document.body.removeChild(field);
        return copied;
    }
}
